import apiClient from "config/apiClient";
import {
  InventorySearchParams,
  MstItemDetailTireSelect,
  MstProcFeeUpdate,
  OrderBoCostFetchRequest,
  OrderBoCostFetchResponse,
  ProcFeeSelectRequest,
  SelectedTireProcFeeParams,
} from "features/mst_proc_fee/types/proc-fee";
import { TireResponse } from "features/car_model/api/carModelApi";

const PROC_FEE_URL = {
  PROC_FEE_SELECT: "/mst/proc/fee/select",
  PROC_FEE_UPDATE: "/mst/proc/fee/update",
  ITEM_DETAIL_TIRE_SELECT: "/mst/item/detail/tire/select",
  SET_SELECT_TIRE_DETAIL: "/mst/item/detail/wheel/tire/set/select",
  INVENTORY_SEARCH: "/stock/select/estimate",
  ORDER_BO_COST_FETCH: "/order/bo/cost/fetch",
};
/**
 * Fetches a list of processing fees based on the provided search parameters.
 * @param {ProcFeeSelectRequest} params - The search parameters for fetching processing fees.
 */
const fetchProcFees = async (params?: ProcFeeSelectRequest): Promise<any> => {
  const searchParams = {
    delete_flag: params?.delete_flag || 0,
  };
  const response = await apiClient.post(PROC_FEE_URL.PROC_FEE_SELECT, searchParams);
  return response.data;
};

/**
 * Updates the processing fee details.
 * @param {MstProcFeeUpdate} procFee - The processing fee object containing updated details.
 * @returns {Promise<any>} The data from the API response.
 * @throws Will throw an error if the request fails.
 */
const updateProcFee = async (procFee: MstProcFeeUpdate): Promise<any> => {
  const response = await apiClient.post(PROC_FEE_URL.PROC_FEE_UPDATE, procFee);
  return response.data;
};

/**
 * Fetches item detail tire selection data from the API.
 * @param {MstItemDetailTireSelect} body - The request payload for item detail tire selection.
 * @returns {Promise<any>} The data from the API response.
 */
const getItemDetailTireSelectApi = async (body: MstItemDetailTireSelect): Promise<any> => {
  const response = await apiClient.post(PROC_FEE_URL.ITEM_DETAIL_TIRE_SELECT, body);
  return response.data;
};

/**
 * Fetches inventory search data from the API.
 * @param {InventorySearchParams} body - The request payload for inventory search.
 * @returns {Promise<any>} The data from the API response.
 */
const postInventorySearchApi = async (body: InventorySearchParams): Promise<any> => {
  const response = await apiClient.post(PROC_FEE_URL.INVENTORY_SEARCH, body);
  return response.data;
};

/**
 * Fetches item detail tire selection data from the API.
 * @param {SelectedTireProcFeeParams} body - The request payload for item detail tire selection.
 * @returns {Promise<any>} The data from the API response.
 */
const getItemDetailTireSetApi = async (body: SelectedTireProcFeeParams): Promise<any> => {
  // const newBody = {
  //   ...body,
  //   categories: body.categories?.join(",") || undefined,
  //   maker_names: body.maker_names?.join(",") || undefined,
  // }
  const response = await apiClient.post<TireResponse>(PROC_FEE_URL.SET_SELECT_TIRE_DETAIL, body);
  return response.data;
};

/**
 * Fetches order BO cost data from the API.
 * @param {OrderBoCostFetchRequest} body - The request payload containing item list with store_cd and item_cd.
 * @returns {Promise<OrderBoCostFetchResponse>} The data from the API response containing cost_price for each item.
 */
const fetchOrderBoCostApi = async (
  body: OrderBoCostFetchRequest,
): Promise<OrderBoCostFetchResponse> => {
  const response = await apiClient.post<OrderBoCostFetchResponse>(
    PROC_FEE_URL.ORDER_BO_COST_FETCH,
    body,
  );
  return response.data as OrderBoCostFetchResponse;
};

/**
 * Helper function to fetch cost prices for multiple items (tires or wheels).
 * Callers decide which items to include based on the response-level
 * price_setting_wheel / price_setting_wheel_low_price flags.
 * @param items - Array of items to fetch cost prices for
 * @param store_cd - Store code to use for the request
 * @returns Promise<Record<string, number>> - Map of item_cd to cost_price
 */
const COST_FETCH_CHUNK_SIZE = 100;

const fetchCostPricesForTires = async (
  items: Array<{ item_cd: string }>,
  store_cd: string,
): Promise<Record<string, number>> => {
  if (items.length === 0) {
    return {};
  }

  const chunks: OrderBoCostFetchRequest[] = [];
  for (let i = 0; i < items.length; i += COST_FETCH_CHUNK_SIZE) {
    chunks.push({
      item_list: items.slice(i, i + COST_FETCH_CHUNK_SIZE).map((item) => ({
        store_cd,
        item_cd: item.item_cd,
      })),
    });
  }

  const results = await Promise.all(
    chunks.map((request) =>
      fetchOrderBoCostApi(request).catch((error) => {
        if (error instanceof Error) {
          // eslint-disable-next-line no-console
          console.error("Error fetching cost prices:", error.message);
        }
        return { item_list: [] } as OrderBoCostFetchResponse;
      }),
    ),
  );

  const costPriceMap: Record<string, number> = {};
  results.forEach((response) => {
    (response.item_list || []).forEach((item) => {
      if (item?.item_cd && item?.cost_price !== undefined) {
        costPriceMap[item.item_cd] = item.cost_price;
      }
    });
  });

  return costPriceMap;
};

export {
  fetchProcFees,
  updateProcFee,
  getItemDetailTireSelectApi,
  getItemDetailTireSetApi,
  postInventorySearchApi,
  fetchOrderBoCostApi,
  fetchCostPricesForTires,
};
